import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

const MatchingWidget = ({ contactId, recherches }) => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // lot DK5 — à la création, les recherches en cours de saisie servent d'aperçu.
  // ⚠️ dépendance STABLE (chaîne), sinon un tableau recréé à chaque rendu relance l'appel en boucle.
  const recherchesUtiles = useMemo(() => (recherches || []).filter((r) => {
    if (r.active === false) return false;
    return !!(r.operation || r.profil || r.budget_min || r.budget_max
      || r.surface_min || r.surface_max || (r.villes || []).filter((v) => String(v || '').trim()).length);
  }), [recherches]);
  const signature = JSON.stringify(recherchesUtiles);
  const aperçuPossible = !contactId && recherchesUtiles.length > 0;
  const enCours = useRef(null);

  const agreger = (data) => {
    let allMatches = [];
    if (data.matches_by_recherche) {
      Object.values(data.matches_by_recherche).forEach(rechercheData => {
        if (rechercheData.scored_matches && rechercheData.scored_matches.length > 0) {
          allMatches = [...allMatches, ...rechercheData.scored_matches];
        }
      });

      // Dédupliquer les biens (un bien peut matcher plusieurs recherches)
      // lot DF — on garde l'occurrence au MEILLEUR score, pas la première rencontrée
      const parBien = new Map();
      allMatches.forEach(match => {
        const bienId = match.property?.id || match.property?.reference;
        if (!bienId) return;
        const existant = parBien.get(bienId);
        if (!existant || (match.score || 0) > (existant.score || 0)) parBien.set(bienId, match);
      });
      const uniqueMatches = Array.from(parBien.values());
      uniqueMatches.sort((a, b) => (b.score || 0) - (a.score || 0));
      return uniqueMatches;
    }
    return data.scored_matches || [];
  };

  // Charger les biens correspondants (API V2 - multiples recherches)
  const fetchMatches = useCallback(async () => {
    if (!contactId && !aperçuPossible) { setMatches([]); return; }

    // un seul appel à la fois : le précédent est abandonné
    enCours.current?.abort();
    const controleur = new AbortController();
    enCours.current = controleur;

    setLoading(true);
    try {
      const jeton = localStorage.getItem('token');
      const response = contactId
        ? await fetch(`${API_URL}/api/contacts/matching-v2/${contactId}`, { signal: controleur.signal })
        : await fetch(`${API_URL}/api/contacts/matching-v2/apercu`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jeton}` },
            body: signature ? `{"recherches":${signature}}` : '{"recherches":[]}',
            signal: controleur.signal,
          });
      if (response.ok) setMatches(agreger(await response.json()));
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Erreur rapprochement:', err);
    } finally {
      if (enCours.current === controleur) setLoading(false);
    }
  }, [contactId, API_URL, aperçuPossible, signature]);

  useEffect(() => {
    if (contactId) { fetchMatches(); return undefined; }
    if (!aperçuPossible) { setMatches([]); return undefined; }
    // lot DK5 — 800 ms après la dernière frappe, pas une requête par caractère
    const minuteur = setTimeout(fetchMatches, 800);
    return () => clearTimeout(minuteur);
  }, [contactId, aperçuPossible, fetchMatches]);

  // Format du prix (adapté pour locations et ventes)
  const formatPrice = (property) => {
    // Pour les locations
    if (property.operation === 'location' || property.operation === 'location_droit_entree' || property.operation === 'location_gerance') {
      const loyer = property.informations_bail?.loyer_mensuel;
      if (!loyer) return 'Loyer NC';
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(loyer) + '/mois';
    }
    // Pour les ventes
    const prix = property.prix_de_presentation || 
                 property.honoraires_calcul?.prix_vente || 
                 property.informations_vente?.prix_annonce;
    if (!prix) return 'Prix NC';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(prix);
  };

  // Obtenir la ville
  const getVille = (property) => {
    return property.public_location?.city || 
           property.informations_confidentielles?.ville || 
           'Ville NC';
  };

  // Obtenir l'icône du profil
  const getProfilIcon = (profil) => {
    switch (profil) {
      case 'chr': return 'bi-egg-fried';
      case 'retail': return 'bi-shop';
      case 'bureaux': return 'bi-building';
      case 'activite_logistique': return 'bi-truck';
      default: return 'bi-house-door-fill';
    }
  };

  // Naviguer vers le bien
  const goToProperty = (propertyId) => {
    window.location.href = `/properties/${propertyId}/view`;
  };

  return (
    <div className="bg-gradient-to-br from-slate-800 to-slate-700 text-white rounded-xl shadow-lg p-4"
         data-testid="widget-rapprochement">
      {/* Header */}
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <i className="bi bi-lightning-charge-fill text-yellow-400 text-lg"></i>
          <h3 className="text-sm font-bold">Rapprochement</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
            matches.length > 0 ? 'bg-green-500 text-white' : 'bg-white/20 text-white'
          }`} data-testid="widget-rapprochement-compteur">
            {loading ? '...' : `${matches.length} Opportunité${matches.length > 1 ? 's' : ''}`}
          </span>
          {matches.length > 0 && (
            <i className={`bi ${expanded ? 'bi-chevron-up' : 'bi-chevron-down'} text-white/60`}></i>
          )}
        </div>
      </div>

      {/* Contenu */}
      {!contactId && !aperçuPossible ? (
        <div className="text-center py-6 text-white/60">
          <i className="bi bi-search text-3xl mb-2 block"></i>
          <p className="text-xs">Renseignez une recherche pour voir les biens correspondants</p>
        </div>
      ) : loading ? (
        <div className="text-center py-6 text-white/60">
          <i className="bi bi-arrow-repeat animate-spin text-2xl mb-2 block"></i>
          <p className="text-xs">Recherche en cours...</p>
        </div>
      ) : matches.length === 0 ? (
        <div className="text-center py-6 text-white/60">
          <i className="bi bi-search text-3xl mb-2 block"></i>
          <p className="text-sm">Aucun bien correspondant</p>
          <p className="text-xs mt-1">Ajoutez des critères de recherche pour activer le rapprochement</p>
        </div>
      ) : (
        <>
          {/* Aperçu des premiers biens (toujours visible) */}
          <div className="mt-3 space-y-2">
            {matches.slice(0, expanded ? matches.length : 2).map((match, idx) => {
              const property = match.property;
              const isLocation = property.operation === 'location' || property.operation === 'location_droit_entree' || property.operation === 'location_gerance';
              const droitEntree = property.informations_bail?.droit_entree || property.informations_bail?.pas_de_porte;
              return (
                <div 
                  key={idx}
                  className="p-2.5 bg-white/10 hover:bg-white/20 rounded-lg cursor-pointer transition"
                  onClick={() => goToProperty(property.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <i className={`bi ${getProfilIcon(property.profil)} text-base`}></i>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">
                          {property.sous_profil || property.profil || 'Bien'}
                        </div>
                        <div className="text-xs text-white/60">
                          <span><i className="bi bi-geo-alt-fill mr-1"></i>{getVille(property)}</span>
                        </div>
                        <div className="text-xs mt-0.5">
                          <span className="text-green-400 font-medium">{formatPrice(property)}</span>
                          {isLocation && droitEntree && (
                            <span className="text-amber-400 font-medium ml-1">
                              + {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(droitEntree)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-300 rounded text-xs font-bold">
                        {match.score}%
                      </span>
                      <i className="bi bi-chevron-right text-white/40"></i>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bouton voir plus */}
          {matches.length > 2 && !expanded && (
            <button 
              className="w-full mt-2 py-1.5 text-xs text-white/60 hover:text-white transition"
              onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
            >
              + {matches.length - 2} autre{matches.length - 2 > 1 ? 's' : ''} bien{matches.length - 2 > 1 ? 's' : ''}
            </button>
          )}

          {/* Bouton actualiser */}
          <button 
            className="w-full mt-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-xs font-medium transition flex items-center justify-center gap-2"
            onClick={(e) => { e.stopPropagation(); fetchMatches(); }}
          >
            <i className={`bi bi-arrow-repeat ${loading ? 'animate-spin' : ''}`}></i>
            Actualiser le rapprochement
          </button>
        </>
      )}
    </div>
  );
};

export default MatchingWidget;
